import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api, formatApiError, resolveMediaSrc } from '@/lib/api';
import { useContent } from '@/lib/content';
import { ImageUploadField } from '@/components/admin/ImageUploadField';

const inputCls = 'w-full bg-ink border border-white/15 px-3 py-2.5 text-sm text-cream placeholder:text-white/30 focus:border-champagne focus:outline-none transition-colors';

const FIELDS = [
  { name: 'tagline', label: 'Tagline' },
  { name: 'phoneDisplay', label: 'Phone (Display)' },
  { name: 'phone', label: 'Phone (Dial format, e.g. +917013095204)' },
  { name: 'whatsapp', label: 'WhatsApp Number (e.g. 917013095204)' },
  { name: 'email', label: 'Email' },
  { name: 'instagram', label: 'Instagram URL' },
  { name: 'youtube', label: 'YouTube URL' },
  { name: 'city', label: 'City / Location' },
];

export const SettingsTab = () => {
  const { settings, refresh } = useContent();
  const [form, setForm] = useState({ ...settings, stats: settings.stats || [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({ ...settings, stats: settings.stats || [] });
  }, [settings]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setStat = (i, k) => (e) => {
    const stats = form.stats.map((s, idx) => (idx === i ? { ...s, [k]: k === 'value' ? Number(e.target.value) || 0 : e.target.value } : s));
    setForm({ ...form, stats });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setError('');
    try {
      await api.put('/admin/settings', form);
      await refresh();
      setMsg('Settings saved — live on the website.');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="admin-settings-form" onSubmit={save}>
      <p className="text-white/50 text-sm mb-6">Brand, contact and homepage settings. Changes go live instantly.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <ImageUploadField label="Hero Background Image" testid="settings-field-heroImage" value={form.heroImage} onChange={(url) => setForm({ ...form, heroImage: url })} />
        <ImageUploadField label="Founder Photo" testid="settings-field-founderImage" value={form.founderImage} onChange={(url) => setForm({ ...form, founderImage: url })} />
        {FIELDS.map((f) => (
          <div key={f.name} className={f.name.includes('Image') || f.name === 'tagline' ? 'sm:col-span-2' : ''}>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">{f.label}</label>
            <input data-testid={`settings-field-${f.name}`} className={inputCls} value={form[f.name] ?? ''} onChange={set(f.name)} />
          </div>
        ))}
      </div>

      <p className="mt-8 mb-4 text-[10px] uppercase tracking-[0.25em] text-champagne">Trust Statistics</p>
      <div className="grid gap-3">
        {form.stats.map((s, i) => (
          <div key={i} className="grid grid-cols-[90px_70px_1fr] gap-3">
            <input data-testid={`settings-stat-value-${i}`} type="number" className={inputCls} value={s.value} onChange={setStat(i, 'value')} />
            <input data-testid={`settings-stat-suffix-${i}`} className={inputCls} value={s.suffix} onChange={setStat(i, 'suffix')} placeholder="+" />
            <input data-testid={`settings-stat-label-${i}`} className={inputCls} value={s.label} onChange={setStat(i, 'label')} placeholder="Label" />
          </div>
        ))}
      </div>

      {form.heroImage && <img src={resolveMediaSrc(form.heroImage)} alt="Hero preview" className="mt-6 h-32 object-cover border border-white/10" onError={(e) => { e.target.style.display = 'none'; }} />}
      {msg && <p data-testid="settings-saved" className="mt-5 text-sm text-champagne">{msg}</p>}
      {error && <p className="mt-5 text-sm text-red-400">{error}</p>}
      <button data-testid="settings-save-btn" type="submit" disabled={busy} className="mt-7 inline-flex items-center gap-2 bg-champagne text-ink px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-cream transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save Settings
      </button>
    </form>
  );
};
